import { useTranslation } from 'react-i18next'
import { HugeiconsIcon } from '@hugeicons/react'
import { Attachment01SolidRounded } from '@hugeicons-pro/core-solid-rounded'
import { AlertCircleStrokeRounded, Attachment01StrokeRounded } from '@hugeicons-pro/core-stroke-rounded'
import { Box, Button } from '@mui/material'
import PropTypes from 'prop-types'

import { ATTACHMENT_SCAN_STATE } from '@/common/utils/Constants/Constants'

// The button sits inside the accordion header, which toggles on click and focus.
const stopPropagation = (event) => event.stopPropagation()

/**
 * DocumentsButton - The attachments control on a quote item, in the item card header and on the
 * review table. A text button that reads "2 Attachments": the count of every attachment, whatever
 * its scan state, then the word, plus a red marker when a file was not accepted. With no
 * attachments it shows no number.
 *
 * @param {Object} props
 * @param {Array} [props.attachments] - the item's DocumentDto rows
 * @param {Function} props.onClick
 */
const DocumentsButton = ({ attachments, onClick }) => {
    const { t } = useTranslation()
    const attachmentCount = attachments?.length ?? 0
    const hasFailedAttachment = !!attachments?.some(({ scanState }) => scanState === ATTACHMENT_SCAN_STATE.Failed)

    const handleClick = (event) => {
        event.stopPropagation()
        onClick()
    }

    const countLabel =
        attachmentCount === 1
            ? t('{{count}} attachment', { count: 1 })
            : t('{{count}} attachments', { count: attachmentCount })
    const label = hasFailedAttachment ? t('{{count}} · a file was not accepted', { count: countLabel }) : countLabel

    return (
        <Button
            aria-label={attachmentCount ? label : t('Add attachments')}
            color="secondary"
            size="small"
            startIcon={
                <HugeiconsIcon
                    icon={attachmentCount ? Attachment01SolidRounded : Attachment01StrokeRounded}
                    size={20}
                />
            }
            sx={{ gap: 0.25, mr: 1, px: 0, whiteSpace: 'nowrap', width: 'fit-content' }}
            variant="text"
            disableRipple
            onClick={handleClick}
            onFocus={stopPropagation}
        >
            {attachmentCount ? (
                <Box
                    component="span"
                    sx={{
                        alignItems: 'center',
                        bgcolor: 'secondary.main',
                        borderRadius: 2.5,
                        color: 'secondary.contrastText',
                        display: 'inline-flex',
                        fontSize: 12,
                        height: 20,
                        justifyContent: 'center',
                        minWidth: 20,
                        mr: 0.75,
                        px: 0.75,
                    }}
                >
                    {attachmentCount}
                </Box>
            ) : null}
            {attachmentCount === 1 ? t('Attachment') : t('Attachments')}
            {hasFailedAttachment ? (
                <Box
                    component="span"
                    data-testid="documents-failed-marker"
                    sx={{ color: 'error.main', display: 'inline-flex' }}
                >
                    <HugeiconsIcon
                        icon={AlertCircleStrokeRounded}
                        size={16}
                    />
                </Box>
            ) : null}
        </Button>
    )
}

DocumentsButton.propTypes = {
    onClick: PropTypes.func.isRequired,
    attachments: PropTypes.array,
}

export default DocumentsButton
